import axios from "axios";
import {catchApiErrors} from "@/helpers/utils";

export class Model {
    static relations = {};

    constructor(attributes) {
        attributes = this.constructor.castRelations(attributes);

        for (const k in attributes) {
            this[k] = attributes[k];
        }
    }

    static castRelations(attributes) {
        const relations = this.relations;

        for (const k in attributes) {
            if (!relations[k] || !attributes[k]) {
                continue;
            }

            const relation = relations[k];

            attributes[k] = Array.isArray(attributes[k]) ? attributes[k].map((v) => new (relation)(v)) : new (relation)(attributes[k]);
        }

        return attributes;
    }

    static primaryKey() {
        return 'id';
    }

    static route() {
        return '';
    }

    save(config = {}) {
        return api.save(this.constructor.route(), this, null, config, 'pk');
    }
}

class apiClass {
    axiosInstance = null;
    processingRequests = {};

    constructor() {
        const user = localStorage.getItem('user');

        const params = {
            withCredentials: true, // required to handle the CSRF token
        };

        if (user) {
            params.headers = {
                common: {
                    Authorization: `Bearer ${JSON.parse(user).token}`
                }
            };
        }

        this.axiosInstance = axios.create(params);

        this.axiosInstance.interceptors.response.use(function (response) {
            // Any status code that lie within the range of 2xx cause this function to trigger
            // Do something with response data
            return response;
        }, function (error) {
            // Any status codes that falls outside the range of 2xx cause this function to trigger
            // Do something with response error

            if (error.response) {
                if (error.response.status === 401) {
                    localStorage.removeItem('user');
                    window.location.href = '/login';
                } else {
                    catchApiErrors(error);
                }
            }

            return Promise.reject(error);
        });
    }

    addProcessingRequest(identifier) {
        this.processingRequests[identifier] = axios.CancelToken.source();
        return this.processingRequests[identifier];
    }

    stopProcessingRequest(identifier) {
        //Check if there are any previous pending requests
        if (this.processingRequests[identifier]) {
            this.processingRequests[identifier].cancel("Operation canceled due to new request.");
            delete this.processingRequests[identifier];
        }
    }

    stopProcessingRequests() {
        for (const i in this.processingRequests) {
            this.processingRequests[i].cancel("Operation canceled due to new request.");
            delete this.processingRequests[i];
        }
    }

    prefixUrl = function (resource) {
        return `/api/${resource}`;
    }

    formatUrl = function (params) {
        return `${this.prefixUrl(params.resource)}${(params && params.id ? ('/' + params.id) : '')}${params.action ? '/' + params.action : ''}`
    }

    serialize(obj, prefix) {
        let str = [], p;
        for (p in obj) {
            if (obj.hasOwnProperty(p)) {
                const k = prefix ? prefix + "[" + p + "]" : p, v = obj[p];

                if (v === null) continue;

                str.push((typeof v === "object") ? this.serialize(v, k) : encodeURIComponent(k) + "=" + encodeURIComponent(v));
            }
        }
        return str.join("&");
    }

    get(resource, params = {}, action = null, config = {}, identifier = null, key = 'id') {
        if (identifier) {
            this.stopProcessingRequest(identifier)
            config.cancelToken = this.addProcessingRequest(identifier).token
        }

        const resourceId = params && params[key] ? params[key] : null;

        if (params && params.hasOwnProperty('id')) {
            delete params[key];
        }

        return this.getInner(resource, resourceId, action, params, config);
    }

    async getModels(model, {params = {}, action = '', config = {}, identifier = ''} = {}) {
        if (identifier) {
            this.stopProcessingRequest(identifier)
            config.cancelToken = this.addProcessingRequest(identifier).token
        }

        const pk = model.primaryKey();
        const hasKey = params && params.hasOwnProperty(pk);
        const resourceId = hasKey ? params[pk] : null;

        if (hasKey) {
            delete params[pk];
        }

        return this.getInner(model.route(), resourceId, action, params, config).then((response) => {
            if (hasKey) {
                response.data = new (model)(response.data);
            } else {
                response.data.data = response.data.data.map((v) => new (model)(v));
            }

            return response;
        });
    }

    getInner(resource, resourceId, action, params, config) {
        if (params && params.advanced) {
            for (const i in params.advanced) {
                if (typeof params.advanced[i] !== 'object') {
                    if (!params.advanced[i]) {
                        delete params.advanced[i];
                    }

                    continue;
                }
                for (const i2 in params.advanced[i]) {
                    if (!params.advanced[i][i2]) {
                        delete params.advanced[i][i2];
                    }
                }
            }
        }

        const getParams = this.serialize(params);

        return this.axiosInstance.get(this.formatUrl({
            resource: resource, id: resourceId, action: action
        }) + (getParams ? ('?' + getParams) : ''), config)
    }

    post(resource, params, action = null, config = {}, key = 'id') {
        return this.axiosInstance.post(this.formatUrl({
            resource: resource, id: params && params[key] ? params[key] : null, action: action
        }), params, config);
    }

    patch(resource, params, action = null, config = {}, key = 'id') {
        return this.axiosInstance.patch(this.formatUrl({
            resource: resource, id: params && params[key] ? params[key] : null, action: action
        }), params, config);
    }

    save(resource, params, action = null, config = {}, key = 'id') {
        if (params[key]) return this.patch(resource, params, action, config, key);

        return this.post(resource, params, action, config);
    }

    remove(resource, id, action = null, config = {}) {
        return this.axiosInstance.delete(this.formatUrl({
            resource: resource, id: id, action: action
        }), config);
    }
}

const api = new apiClass();

export default api;